import React, { useEffect, useState } from 'react';
import { DashboardActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';
import T from 'components/common/text/T';
import { icon_refresh } from 'components/base/images';
import MuiButton from 'components/common/button/MuiButton';
import { projectProcessFlagList } from 'lib/mapper';
import styled from 'styled-components';
import CustomTranslateTooltip from 'components/common/tooltip/CustomTranslateTooltip';
import PatientToothTooltip from 'components/dashboard/PatientToothTooltip';
import { useHistory } from 'react-router-dom';
import { useDidUpdateEffect } from 'lib/utils';
import ListEmpty from 'components/common/list/ListEmpty';
import CircularLoading from 'components/base/loading/CircularLoading';
import { PageSubTitle } from 'components/base/title/PageTitle';

interface PatientProjectProps {
  className?: string;
}

function PatientProjectContainer({ className }: PatientProjectProps) {
  const {
    language,
    latestProjects: { list: latestProjects },
    latestProjectsSuccess,
    latestProjectsPending,
    latestProjectsFailure,
  }: {
    language: string;
    latestProjects: { list: [] };
    latestProjectsSuccess: boolean;
    latestProjectsPending: boolean;
    latestProjectsFailure: boolean;
  } = useShallowAppSelector(state => ({
    language: state.base.language,
    latestProjects: state.dashboard.latestProjects.data || { list: [] },
    latestProjectsSuccess: state.dashboard.latestProjects.success,
    latestProjectsPending: state.dashboard.latestProjects.pending,
    latestProjectsFailure: state.dashboard.latestProjects.failure,
  }));

  const { t } = useTranslation();
  const history = useHistory();
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);

  useEffect(() => {
    DashboardActions.fetch_latest_projects_request();
    return () => {
      setIsLoadSuccess(false);
      setIsLoadFailure(false);
    };
  }, []);

  const handleRefreshList = () => {
    DashboardActions.fetch_latest_projects_request();
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  const handleClickProject = (projectCode: number) => {
    history.push(`/project/detail/${projectCode}`);
  };

  useDidUpdateEffect(() => {
    if (!latestProjectsSuccess) return;
    setIsLoadSuccess(true);
  }, [latestProjectsSuccess]);

  useDidUpdateEffect(() => {
    if (!latestProjectsFailure) return;
    setIsLoadFailure(true);
  }, [latestProjectsFailure]);

  return (
    <StyledPatientProject data-component-name="Patient" className={className} lang={language}>
      <div className="dashboard__card_title_box">
        <h2 className="dashboard__card_title">
          <PageSubTitle title="HOME_TITLE_NEW_PROJECTS" margin="0" />
        </h2>
        <MuiButton
          config={{
            bgColor: '#fff',
            borderColor: '#e1e1e1',
            width: '50px',
          }}
          className="xs dashboard__card_refresh"
          variant="outlined"
          onClick={handleRefreshList}
        >
          <img src={icon_refresh} />
        </MuiButton>
      </div>

      <div className="dashboard__patient_project_wrapper box-shadow-default">
        <div className="dashboard__patient_project_container">
          {latestProjects.length < 1 && latestProjectsPending && (
            <div className="infinite-scroll-loading">
              <CircularLoading />
            </div>
          )}
          {!!isLoadSuccess &&
            (latestProjects.length > 0 ? (
              latestProjects.map((item: any, idx: number) => {
                return (
                  <PatientProjectListItem
                    key={idx}
                    className="dashboard__patient_project_item_box"
                    onClickProject={handleClickProject}
                    project={item}
                  />
                );
              }, [])
            ) : (
              <ListEmpty text="GLOBAL_EMPTY_LIST_DATA" />
            ))}
          {!!isLoadFailure && <ListEmpty text="GLOBAL_LIST_ERROR" isRefreshButton={true} />}
        </div>
      </div>
    </StyledPatientProject>
  );
}

const StyledPatientProject = styled.section<{}>`
  .dashboard__card_title_box {
    display: flex;
    .dashboard__card_refresh {
      height: 34px;
    }
  }
  .dashboard__patient_project_wrapper {
    width: 395px;
    height: 360px;
    background-color: white;
    border-radius: 10px;
    .dashboard__patient_project_container {
      position: relative;
      height: 360px;
      overflow: overlay;
      .dashboard__patient_project_item_box {
        border-bottom: 1px solid #e1e1e1;
        &:not(:last-child) {
        }
      }
    }
  }
`;

interface StyledPatientProjectListItemProps {
  pItem?: any;
  className?: string;
  onClickProject: (projectCode: number) => void;
  project: any;
}

function PatientProjectListItem({
  pItem,
  className,
  onClickProject,
  project,
}: StyledPatientProjectListItemProps) {
  const { t } = useTranslation();
  const fullName = `${!!project?.firstName ? project.firstName : ''} ${
    !!project?.lastName ? project.lastName : ''
  }${!!project?.identification ? project.identification : ''}`;
  const teeth = project?.teeth;

  return (
    <StyledPatientProjectListItem
      data-component-name="PatientProjectItem"
      className={className}
      onClick={() => {
        onClickProject(project?.projectCode);
      }}
    >
      <div className="patientProjectItem__status_box">
        <div
          className="patientProjectItem__status"
          style={{
            backgroundColor: projectProcessFlagList.find((item: any) => item.id === 2)?.color,
          }}
        ></div>
      </div>
      <div className="patientProjectItem__info_box">
        <div className="patientProjectItem__info_row patient">
          <div className="patient__name_box">
            <div className="patient__name text-overflow-ellipsis">{fullName}</div>
          </div>
          <div className="patient__code">{project?.chartNumber}</div>
        </div>
        <div className="patientProjectItem__info_row project">
          <div className="project__name_box">
            <div className="project__name text-overflow-ellipsis">{project?.projectId}</div>
          </div>
          <div className="project__indication_box">
            <CustomTranslateTooltip
              title={<PatientToothTooltip data={teeth} />}
              top="2px"
              width="310px"
              placement="bottom-end"
            >
              <div className="project__indication">
                <StyledPatientTeethListItem color={teeth[0]?.color}>
                  {teeth[0]?.toothNumber}
                </StyledPatientTeethListItem>
                {projectProcessFlagList.length > 1 && (
                  <div className="custom_ellipsis_box">
                    <div className="custom_ellipsis">..</div>
                  </div>
                )}
              </div>
            </CustomTranslateTooltip>
          </div>
        </div>
      </div>
    </StyledPatientProjectListItem>
  );
}

const StyledPatientProjectListItem = styled.section<{}>`
  height: 90px;
  padding: 15px 20px;
  display: flex;
  column-gap: 10px;
  position: relative;
  &:hover {
    cursor: pointer;
    background-color: #f4f4f4;
  }
  .patientProjectItem__status_box {
    .patientProjectItem__status {
      width: 5px;
      height: 100%;
    }
  }
  .patientProjectItem__info_box {
    width: calc(100% - 5px);
    display: flex;
    flex-direction: column;
    row-gap: 3px;
    .patientProjectItem__info_row {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &.patient {
        padding-top: 2px;
        height: 26px;
        .patient__name_box {
          width: 210px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          .patient__name {
            width: 100%;
            height: 20px;
            line-height: 20px;
            font-size: 14px;
            color: #444444;
            font-weight: bold;
          }
        }
        .patient__code {
          width: 130px;
          height: 20px;
          line-height: 20px;
          font-size: 14px;
          color: #b6b6b6;
          display: flex;
          justify-content: flex-end;
        }
      }
      &.project {
        height: 30px;
        .project__name_box {
          width: 300px;
          height: 100%;
          padding: 6px 0 4px 0;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          .project__name {
            width: 100%;
            height: 20px;
            line-height: 20px;
            font-size: 13px;
            color: #444444;
          }
        }
        .project__indication_box {
          width: 40px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          padding-left: 4px;
          .project__indication {
            width: 36px;
            height: 26px;
            display: flex;
            .project__indication_item {
              width: 26px;
              height: 26px;
              border: 1px solid #614ab8;
              color: #614ab8;
              border-radius: 50%;
              font-size: 14px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .custom_ellipsis_box {
              height: 26px;
              display: flex;
              align-items: flex-end;
            }
          }
        }
      }
    }
  }
`;
const StyledPatientTeethListItem = styled.div<{ color: string }>`
  width: 26px;
  height: 26px;
  border: ${({ color }) => `1px solid ${color}`};
  color: ${({ color }) => `${color}`};
  border-radius: 50%;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default React.memo(PatientProjectContainer);
